import React, { useState } from 'react';
import { useLocation, Link, Routes, Route, Navigate } from 'react-router-dom';
import ApiService from '../../services/api';
import { getToken } from '../../utils/auth';
import { useNotification } from '../../context/NotificationContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

// Financial term explanations
type FinancialTermKey =
  | 'marketCap'
  | 'enterpriseValue'
  | 'peRatio'
  | 'pbRatio'
  | 'revenue'
  | 'netIncome'
  | 'eps'
  | 'totalAssets'
  | 'totalDebt'
  | 'cash'
  | 'grossMargin'
  | 'operatingMargin'
  | 'netMargin'
  | 'roa'
  | 'roe'
  | 'currentRatio'
  | 'quickRatio'
  | 'debtEquity'
  | 'assetTurnover'
  | 'inventoryTurnover'
  | 'receivablesTurnover'
  | 'payoutRatio'
  | 'priceToSales'
  | 'priceToBook'
  | 'priceToTangibleBook'
  | 'priceToCashFlow'
  | 'beta'
  | 'revenueGrowth'
  | 'epsGrowth'
  | 'dividendYield'
  | 'ebitdaMargin'
  | 'fcfMargin'
  | 'roi'
  | 'revenuePerEmployee'
  | 'netIncomePerEmployee'
  | 'interestCoverage'
  | 'dividendGrowth'
  | 'priceReturn5D'
  | 'priceReturnMTD'
  | 'priceReturn3M'
  | 'priceReturnYTD'
  | 'priceReturn1Y'
  | 'high52Week'
  | 'low52Week'
  | 'avgVolume10D'
  | 'avgVolume3M'
  | 'relativeReturn1M'
  | 'relativeReturn3M'
  | 'relativeReturn6M'
  | 'relativeReturn1Y';

const financialTerms: Record<FinancialTermKey, string> = {
  marketCap:
    "Total value of a company's shares of stock. Calculated by multiplying the price of a stock by its total number of outstanding shares.",
  enterpriseValue:
    'Total value of a company including its market cap and debt, minus cash.',
  peRatio:
    'Price-to-Earnings ratio. Shows how much investors are willing to pay per dollar of earnings.',
  pbRatio:
    "Price-to-Book ratio. Compares a company's market value to its book value.",
  revenue: 'Total amount of income generated from sales of goods or services.',
  netIncome: "Company's total earnings or profit.",
  eps: "Earnings Per Share. Company's profit divided by outstanding shares of stock.",
  totalAssets: 'Sum of all assets owned by the company.',
  totalDebt: 'Sum of all short-term and long-term debt obligations.',
  cash: 'Cash and cash equivalents readily available.',
  grossMargin: 'Percentage of revenue remaining after cost of goods sold.',
  operatingMargin: 'Percentage of revenue remaining after operating expenses.',
  netMargin: 'Percentage of revenue that becomes profit.',
  roa: 'Return on Assets. Shows how efficiently a company uses assets to generate earnings.',
  roe: "Return on Equity. Shows how efficiently a company uses shareholders' investments.",
  currentRatio: "Measures company's ability to pay short-term obligations.",
  quickRatio:
    'Similar to current ratio but excludes inventory from current assets.',
  debtEquity:
    "Total debt divided by shareholders' equity, shows financial leverage.",
  assetTurnover:
    'Measures how efficiently a company uses its assets to generate sales.',
  inventoryTurnover:
    'Number of times inventory is sold and replaced over a period.',
  receivablesTurnover:
    'Number of times receivables are collected over a period.',
  payoutRatio: 'Percentage of earnings paid as dividends.',
  priceToSales:
    'Price-to-Sales ratio. Compares stock price to revenue per share.',
  priceToBook: 'Price-to-Book ratio. Compares market value to book value.',
  priceToTangibleBook: 'Price to tangible book value ratio.',
  priceToCashFlow: 'Price to cash flow ratio.',
  beta: "Measure of stock's volatility compared to the market.",
  revenueGrowth: 'Year-over-year growth in revenue.',
  epsGrowth: 'Year-over-year growth in earnings per share.',
  dividendYield: 'Annual dividend payments relative to stock price.',
  ebitdaMargin: 'EBITDA as a percentage of revenue.',
  fcfMargin: 'Free cash flow as a percentage of revenue.',
  roi: 'Return on Investment. Shows how efficiently a company uses its capital.',
  revenuePerEmployee: 'Total revenue divided by number of employees.',
  netIncomePerEmployee: 'Net income divided by number of employees.',
  interestCoverage: 'Operating income divided by interest expenses.',
  dividendGrowth: '5-year compound annual growth rate of dividends.',
  priceReturn5D: 'Stock price return over the last 5 trading days.',
  priceReturnMTD: 'Stock price return month to date.',
  priceReturn3M: 'Stock price return over the last 3 months.',
  priceReturnYTD: 'Stock price return year to date.',
  priceReturn1Y: 'Stock price return over the last year.',
  high52Week: 'Highest stock price in the last 52 weeks.',
  low52Week: 'Lowest stock price in the last 52 weeks.',
  avgVolume10D: 'Average daily trading volume over 10 days.',
  avgVolume3M: 'Average daily trading volume over 3 months.',
  relativeReturn1M: 'Stock return relative to S&P 500 over 1 month.',
  relativeReturn3M: 'Stock return relative to S&P 500 over 3 months.',
  relativeReturn6M: 'Stock return relative to S&P 500 over 6 months.',
  relativeReturn1Y: 'Stock return relative to S&P 500 over 1 year.',
};

// Earnings Calendar Component
const EarningsCalendar = () => {
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [earnings, setEarnings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getEarningsCalendar(fromDate, toDate);
      setEarnings(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching earnings calendar:', error);
      showNotification(
        'Failed to fetch earnings calendar. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Earnings Calendar</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Earnings Calendar'}
            </button>
          </div>
        </div>
      </form>

      {earnings.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EPS Estimate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EPS Actual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue Estimate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue Actual
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {earnings.map((earning, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {earning.symbol}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {earning.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    ${earning.epsEstimate?.toFixed(2) || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    ${earning.epsActual?.toFixed(2) || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    ${earning.revenueEstimate?.toLocaleString() || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    ${earning.revenueActual?.toLocaleString() || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Analyst Recommendations Component
const AnalystRecommendations = () => {
  const [symbol, setSymbol] = useState<string>('');
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getAnalystRecommendations(symbol);
      setRecommendations(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching analyst recommendations:', error);
      showNotification(
        'Failed to fetch analyst recommendations. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (rec: any) => {
    // Weight: Strong Buy (2), Buy (1), Hold (0), Sell (-1), Strong Sell (-2)
    return (
      rec.strongBuy * 2 +
      rec.buy * 1 +
      rec.hold * 0 +
      rec.sell * -1 +
      rec.strongSell * -2
    );
  };

  const calculateTotal = (rec: any) => {
    return rec.strongBuy + rec.buy + rec.hold + rec.sell + rec.strongSell;
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Analyst Recommendations</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL"
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Recommendations'}
            </button>
          </div>
        </div>
      </form>

      {recommendations.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Strong Buy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Buy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sell
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Strong Sell
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recommendations.map((rec, index) => {
                const total = calculateTotal(rec);
                const score = calculateScore(rec);

                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(rec.period).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-green-700 font-medium">
                      {rec.strongBuy} (
                      {((rec.strongBuy / total) * 100).toFixed(1)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-green-600">
                      {rec.buy} ({((rec.buy / total) * 100).toFixed(1)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {rec.hold} ({((rec.hold / total) * 100).toFixed(1)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-red-600">
                      {rec.sell} ({((rec.sell / total) * 100).toFixed(1)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-red-700 font-medium">
                      {rec.strongSell} (
                      {((rec.strongSell / total) * 100).toFixed(1)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {total}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap font-medium ${
                        score > 0
                          ? 'text-green-600'
                          : score < 0
                            ? 'text-red-600'
                            : 'text-gray-600'
                      }`}
                    >
                      {score > 0 ? '+' : ''}
                      {score}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// BasicFinancials Component
const BasicFinancials = () => {
  const [symbol, setSymbol] = useState<string>('');
  const [financials, setFinancials] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();
  const [activeMetric, setActiveMetric] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getBasicFinancial(symbol);
      setFinancials(result);
    } catch (error) {
      console.error('Error fetching basic financials:', error);
      showNotification(
        'Failed to fetch basic financials. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const renderMetricWithTooltip = (
    label: string,
    value: any,
    key: FinancialTermKey,
  ) => (
    <div
      className="relative group"
      onMouseEnter={() => setActiveMetric(key)}
      onMouseLeave={() => setActiveMetric(null)}
    >
      <div className="flex justify-between items-center">
        <span className="font-medium">{label}:</span>
        <span className="ml-2">
          {typeof value === 'number'
            ? value > 1000000
              ? `$${(value / 1000000).toFixed(2)}M`
              : value > 1
                ? value.toFixed(2)
                : value.toLocaleString()
            : 'N/A'}
        </span>
      </div>
      {activeMetric === key && (
        <div className="absolute z-10 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs">
          {financialTerms[key]}
        </div>
      )}
    </div>
  );

  const renderTimeSeriesChart = (seriesData: any[], title: string) => {
    if (!seriesData || seriesData.length === 0) return null;

    // Sort data by date in ascending order
    const sortedData = [...seriesData].sort(
      (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime(),
    );

    const data = {
      labels: sortedData.map((d) =>
        new Date(d.period).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
        }),
      ),
      datasets: [
        {
          label: title,
          data: sortedData.map((d) => d.v),
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: title,
        },
      },
    };

    return (
      <div className="mt-4">
        <Line data={data} options={options} />
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Basic Financials</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL"
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Basic Financials'}
            </button>
          </div>
        </div>
      </form>

      {financials && (
        <div className="bg-white p-4 rounded shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-3">Valuation Metrics</h3>
              <div className="space-y-2">
                {renderMetricWithTooltip(
                  'Market Cap',
                  financials.metric.marketCapitalization,
                  'marketCap',
                )}
                {renderMetricWithTooltip(
                  'Enterprise Value',
                  financials.metric.enterpriseValue,
                  'enterpriseValue',
                )}
                {renderMetricWithTooltip(
                  'P/E Ratio',
                  financials.metric.peAnnual,
                  'peRatio',
                )}
                {renderMetricWithTooltip(
                  'P/B Ratio',
                  financials.metric.pbAnnual,
                  'pbRatio',
                )}
                {renderMetricWithTooltip(
                  'P/S Ratio',
                  financials.metric.psTTM,
                  'priceToSales',
                )}
                {renderMetricWithTooltip(
                  'P/CF Ratio',
                  financials.metric.pcfShareTTM,
                  'priceToCashFlow',
                )}
                {renderMetricWithTooltip(
                  'P/TBV Ratio',
                  financials.metric.ptbvAnnual,
                  'priceToTangibleBook',
                )}
                {renderMetricWithTooltip(
                  'Beta',
                  financials.metric.beta,
                  'beta',
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3">Growth & Returns</h3>
              <div className="space-y-2">
                {renderMetricWithTooltip(
                  'Revenue Growth (YoY)',
                  financials.metric.revenueGrowthTTMYoy,
                  'revenueGrowth',
                )}
                {renderMetricWithTooltip(
                  'EPS Growth (YoY)',
                  financials.metric.epsGrowthTTMYoy,
                  'epsGrowth',
                )}
                {renderMetricWithTooltip(
                  'ROE',
                  financials.metric.roeTTM,
                  'roe',
                )}
                {renderMetricWithTooltip(
                  'ROA',
                  financials.metric.roaTTM,
                  'roa',
                )}
                {renderMetricWithTooltip(
                  'ROIC',
                  financials.metric.roiTTM,
                  'roi',
                )}
                {renderMetricWithTooltip(
                  'Revenue/Employee',
                  financials.metric.revenueEmployeeTTM,
                  'revenuePerEmployee',
                )}
                {renderMetricWithTooltip(
                  'Net Income/Employee',
                  financials.metric.netIncomeEmployeeTTM,
                  'netIncomePerEmployee',
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3">
                Margins & Profitability
              </h3>
              <div className="space-y-2">
                {renderMetricWithTooltip(
                  'Gross Margin',
                  financials.metric.grossMarginTTM,
                  'grossMargin',
                )}
                {renderMetricWithTooltip(
                  'Operating Margin',
                  financials.metric.operatingMarginTTM,
                  'operatingMargin',
                )}
                {renderMetricWithTooltip(
                  'Net Margin',
                  financials.metric.netProfitMarginTTM,
                  'netMargin',
                )}
                {renderMetricWithTooltip(
                  'EBITDA Margin',
                  financials.metric.ebitdaPerShareTTM,
                  'ebitdaMargin',
                )}
                {renderMetricWithTooltip(
                  'FCF Margin',
                  financials.metric.fcfMargin,
                  'fcfMargin',
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3">Efficiency Metrics</h3>
              <div className="space-y-2">
                {renderMetricWithTooltip(
                  'Asset Turnover',
                  financials.metric.assetTurnoverTTM,
                  'assetTurnover',
                )}
                {renderMetricWithTooltip(
                  'Inventory Turnover',
                  financials.metric.inventoryTurnoverTTM,
                  'inventoryTurnover',
                )}
                {renderMetricWithTooltip(
                  'Receivables Turnover',
                  financials.metric.receivablesTurnoverTTM,
                  'receivablesTurnover',
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3">Liquidity & Solvency</h3>
              <div className="space-y-2">
                {renderMetricWithTooltip(
                  'Current Ratio',
                  financials.metric.currentRatioAnnual,
                  'currentRatio',
                )}
                {renderMetricWithTooltip(
                  'Quick Ratio',
                  financials.metric.quickRatioAnnual,
                  'quickRatio',
                )}
                {renderMetricWithTooltip(
                  'Debt/Equity',
                  financials.metric['totalDebt/totalEquityAnnual'],
                  'debtEquity',
                )}
                {renderMetricWithTooltip(
                  'Interest Coverage',
                  financials.metric.netInterestCoverageTTM,
                  'interestCoverage',
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3">Dividend Metrics</h3>
              <div className="space-y-2">
                {renderMetricWithTooltip(
                  'Dividend Yield',
                  financials.metric.dividendYieldIndicatedAnnual,
                  'dividendYield',
                )}
                {renderMetricWithTooltip(
                  'Payout Ratio',
                  financials.metric.payoutRatioTTM,
                  'payoutRatio',
                )}
                {renderMetricWithTooltip(
                  'Dividend Growth (5Y)',
                  financials.metric.dividendGrowthRate5Y,
                  'dividendGrowth',
                )}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-3">Price Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="text-sm font-medium mb-2">Price Returns</h4>
                <div className="space-y-2">
                  {renderMetricWithTooltip(
                    '5 Day',
                    financials.metric['5DayPriceReturnDaily'],
                    'priceReturn5D',
                  )}
                  {renderMetricWithTooltip(
                    '1 Month',
                    financials.metric.monthToDatePriceReturnDaily,
                    'priceReturnMTD',
                  )}
                  {renderMetricWithTooltip(
                    '3 Month',
                    financials.metric['13WeekPriceReturnDaily'],
                    'priceReturn3M',
                  )}
                  {renderMetricWithTooltip(
                    'YTD',
                    financials.metric.yearToDatePriceReturnDaily,
                    'priceReturnYTD',
                  )}
                  {renderMetricWithTooltip(
                    '1 Year',
                    financials.metric['52WeekPriceReturnDaily'],
                    'priceReturn1Y',
                  )}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="text-sm font-medium mb-2">
                  Trading Information
                </h4>
                <div className="space-y-2">
                  {renderMetricWithTooltip(
                    '52 Week High',
                    financials.metric['52WeekHigh'],
                    'high52Week',
                  )}
                  {renderMetricWithTooltip(
                    '52 Week Low',
                    financials.metric['52WeekLow'],
                    'low52Week',
                  )}
                  {renderMetricWithTooltip(
                    '10D Avg Volume',
                    financials.metric['10DayAverageTradingVolume'],
                    'avgVolume10D',
                  )}
                  {renderMetricWithTooltip(
                    '3M Avg Volume',
                    financials.metric['3MonthAverageTradingVolume'],
                    'avgVolume3M',
                  )}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="text-sm font-medium mb-2">
                  Relative to S&P 500
                </h4>
                <div className="space-y-2">
                  {renderMetricWithTooltip(
                    '1 Month',
                    financials.metric['priceRelativeToS&P5004Week'],
                    'relativeReturn1M',
                  )}
                  {renderMetricWithTooltip(
                    '3 Month',
                    financials.metric['priceRelativeToS&P50013Week'],
                    'relativeReturn3M',
                  )}
                  {renderMetricWithTooltip(
                    '6 Month',
                    financials.metric['priceRelativeToS&P50026Week'],
                    'relativeReturn6M',
                  )}
                  {renderMetricWithTooltip(
                    '1 Year',
                    financials.metric['priceRelativeToS&P50052Week'],
                    'relativeReturn1Y',
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-3">Historical Trends</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderTimeSeriesChart(
                financials.series.annual.grossMargin,
                'Gross Margin',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.operatingMargin,
                'Operating Margin',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.netMargin,
                'Net Margin',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.roe,
                'Return on Equity',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.roa,
                'Return on Assets',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.currentRatio,
                'Current Ratio',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.quickRatio,
                'Quick Ratio',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.totalDebtToEquity,
                'Debt to Equity',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.assetTurnoverTTM,
                'Asset Turnover',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.inventoryTurnover,
                'Inventory Turnover',
              )}
              {renderTimeSeriesChart(
                financials.series.annual.receivablesTurnover,
                'Receivables Turnover',
              )}
              {renderTimeSeriesChart(financials.series.annual.eps, 'EPS')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface OptionData {
  symbol: string;
  description: string;
  last: number;
  change: number;
  volume: number;
  bid: number;
  ask: number;
  strike: number;
  greeks: {
    delta: number;
    gamma: number;
    theta: number;
    vega: number;
    rho: number;
    mid_iv: number;
  };
  change_percentage: number;
  open_interest: number;
  option_type: string;
}

const OptionsData = () => {
  const [symbol, setSymbol] = useState<string>('');
  const [expirations, setExpirations] = useState<string[]>([]);
  const [selectedExpiration, setSelectedExpiration] = useState<string>('');
  const [optionChains, setOptionChains] = useState<OptionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [stockPrice, setStockPrice] = useState<number>(0);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getOptionExpirations(symbol);
      console.log('Option expirations response:', result); // Debug log

      // Check if result is an array directly or has a different structure
      if (Array.isArray(result)) {
        setExpirations(result);
      } else if (Array.isArray(result?.date)) {
        setExpirations(result.date);
      } else if (Array.isArray(result?.data)) {
        setExpirations(result.data);
      } else if (Array.isArray(result?.data?.date)) {
        setExpirations(result.data.date);
      } else {
        console.error('Unexpected response format:', result);
        showNotification('Unexpected response format from API', 'error');
        setExpirations([]);
      }

      setSelectedExpiration('');
      setOptionChains([]);
    } catch (error) {
      console.error('Error fetching option expirations:', error);
      showNotification(
        'Failed to fetch option expirations. Please try again.',
        'error',
      );
      setExpirations([]);
    } finally {
      setLoading(false);
    }
  };

  const formatExpirationDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getDaysToExpiration = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expirationDate = new Date(dateStr);
    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleExpirationChange = async (expiration: string) => {
    setSelectedExpiration(expiration);
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getOptionChains(symbol, expiration);
      console.log('Option chains response:', result); // Debug log

      // Handle different possible response formats
      let options: OptionData[] = [];
      if (Array.isArray(result)) {
        options = result;
      } else if (Array.isArray(result?.options)) {
        options = result.options;
      } else if (result?.options && typeof result.options === 'object') {
        // If options is an object containing the options data
        options = Object.values(result.options).flat();
      } else if (Array.isArray(result?.data?.options)) {
        options = result.data.options;
      } else if (
        result?.data?.options &&
        typeof result.data.options === 'object'
      ) {
        // If data.options is an object containing the options data
        options = Object.values(result.data.options).flat();
      }

      if (options.length === 0) {
        console.error('No options data found in response:', result);
        showNotification('No options data available for this date', 'warning');
      } else {
        console.log('Processed options:', options); // Debug log
      }

      setOptionChains(options);

      // Get the current stock price from the response if available
      const stockPriceFromResponse =
        result?.underlying?.price ||
        result?.price ||
        result?.data?.underlying?.price ||
        result?.data?.price ||
        null;
      if (stockPriceFromResponse) {
        setStockPrice(stockPriceFromResponse);
      }
    } catch (error) {
      console.error('Error fetching option chains:', error);
      showNotification(
        'Failed to fetch option chains. Please try again.',
        'error',
      );
      setOptionChains([]);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (
    value: number | undefined,
    isPercentage = false,
    decimals = 2,
  ) => {
    if (value === undefined || value === null) return 'N/A';
    return isPercentage
      ? `${value.toFixed(decimals)}%`
      : value.toFixed(decimals);
  };

  const formatGreek = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return value.toFixed(4);
  };

  const renderOptionsTable = () => {
    if (!optionChains.length) return null;

    const sortedStrikes = [...new Set(optionChains.map((o) => o.strike))].sort(
      (a, b) => a - b,
    );
    const atmStrikeIndex = sortedStrikes.findIndex(
      (strike) => strike >= stockPrice,
    );

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="divide-x divide-gray-200">
              <th
                colSpan={7}
                className="px-4 py-2 text-center bg-green-50 text-green-700"
              >
                Calls
              </th>
              <th className="px-4 py-2 text-center bg-gray-100">Strike</th>
              <th
                colSpan={7}
                className="px-4 py-2 text-center bg-red-50 text-red-700"
              >
                Puts
              </th>
            </tr>
            <tr className="divide-x divide-gray-200 text-xs">
              <th className="px-3 py-2">Last</th>
              <th className="px-3 py-2">Bid</th>
              <th className="px-3 py-2">Ask</th>
              <th className="px-3 py-2">Volume</th>
              <th className="px-3 py-2">OI</th>
              <th className="px-3 py-2">IV</th>
              <th className="px-3 py-2">Greeks</th>
              <th className="px-3 py-2 bg-gray-100 font-bold">Price</th>
              <th className="px-3 py-2">Last</th>
              <th className="px-3 py-2">Bid</th>
              <th className="px-3 py-2">Ask</th>
              <th className="px-3 py-2">Volume</th>
              <th className="px-3 py-2">OI</th>
              <th className="px-3 py-2">IV</th>
              <th className="px-3 py-2">Greeks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white text-sm">
            {sortedStrikes.map((strike, index) => {
              const call = optionChains.find(
                (o) => o.strike === strike && o.option_type === 'call',
              );
              const put = optionChains.find(
                (o) => o.strike === strike && o.option_type === 'put',
              );
              const isAtm = index === atmStrikeIndex;

              return (
                <tr
                  key={strike}
                  className={`
                  divide-x divide-gray-200 hover:bg-gray-50
                  ${isAtm ? 'bg-blue-50' : ''}
                `}
                >
                  {/* Calls */}
                  <td className="px-3 py-2">{formatValue(call?.last)}</td>
                  <td className="px-3 py-2">{formatValue(call?.bid)}</td>
                  <td className="px-3 py-2">{formatValue(call?.ask)}</td>
                  <td className="px-3 py-2">{call?.volume || 0}</td>
                  <td className="px-3 py-2">{call?.open_interest || 0}</td>
                  <td className="px-3 py-2">
                    {formatValue(call?.greeks?.mid_iv, true)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>Δ {formatGreek(call?.greeks?.delta)}</div>
                    <div>Γ {formatGreek(call?.greeks?.gamma)}</div>
                    <div>Θ {formatGreek(call?.greeks?.theta)}</div>
                    <div>V {formatGreek(call?.greeks?.vega)}</div>
                  </td>

                  {/* Strike Price */}
                  <td
                    className={`px-3 py-2 font-bold text-center ${
                      isAtm ? 'bg-blue-100' : 'bg-gray-100'
                    }`}
                  >
                    ${strike.toFixed(2)}
                  </td>

                  {/* Puts */}
                  <td className="px-3 py-2">{formatValue(put?.last)}</td>
                  <td className="px-3 py-2">{formatValue(put?.bid)}</td>
                  <td className="px-3 py-2">{formatValue(put?.ask)}</td>
                  <td className="px-3 py-2">{put?.volume || 0}</td>
                  <td className="px-3 py-2">{put?.open_interest || 0}</td>
                  <td className="px-3 py-2">
                    {formatValue(put?.greeks?.mid_iv, true)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>Δ {formatGreek(put?.greeks?.delta)}</div>
                    <div>Γ {formatGreek(put?.greeks?.gamma)}</div>
                    <div>Θ {formatGreek(put?.greeks?.theta)}</div>
                    <div>V {formatGreek(put?.greeks?.vega)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Options Data</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL"
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Option Expirations'}
            </button>
          </div>
        </div>
      </form>

      {expirations.length > 0 && (
        <div className="mb-6 bg-white p-4 rounded shadow">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Expiration Date
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {expirations.map((exp) => {
              const daysToExp = getDaysToExpiration(exp);
              return (
                <button
                  key={exp}
                  onClick={() => handleExpirationChange(exp)}
                  className={`p-3 text-left rounded border ${
                    selectedExpiration === exp
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium">{formatExpirationDate(exp)}</div>
                  <div className="text-sm text-gray-500">
                    {daysToExp} days to expiration
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {optionChains.length > 0 && (
        <div className="bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Option Chain</h3>
            <div className="text-sm">
              <span className="font-medium">Stock Price:</span>
              <span className="ml-2">${stockPrice.toFixed(2)}</span>
            </div>
          </div>
          {renderOptionsTable()}
        </div>
      )}
    </div>
  );
};

interface FDAEvent {
  fromDate: string;
  toDate: string;
  eventDescription: string;
  url: string;
}

// FDA Calendar Component
const FDACalendar = () => {
  const [events, setEvents] = useState<FDAEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getFDACalendar();
      console.log('FDA Calendar response:', result); // Debug log

      // Handle different possible response formats
      let fdaEvents: FDAEvent[] = [];
      if (Array.isArray(result)) {
        fdaEvents = result;
      } else if (Array.isArray(result?.data)) {
        fdaEvents = result.data;
      } else if (result?.data?.events) {
        fdaEvents = result.data.events;
      }

      // Validate and transform the data
      const validEvents = fdaEvents
        .map((event) => ({
          fromDate: event.fromDate || '',
          toDate: event.toDate || '',
          eventDescription:
            event.eventDescription || 'No description available',
          url: event.url || '#',
        }))
        .filter((event) => event.fromDate && event.toDate);

      console.log('Processed FDA events:', validEvents); // Debug log
      setEvents(validEvents);

      if (validEvents.length === 0) {
        showNotification('No FDA events found for the given period', 'info');
      }
    } catch (error) {
      console.error('Error fetching FDA calendar:', error);
      showNotification(
        'Failed to fetch FDA calendar. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'America/New_York', // FDA times are typically in ET
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">FDA Calendar</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Get FDA Calendar'}
          </button>
        </div>
      </form>

      {events.length > 0 && (
        <div className="space-y-4">
          {events.map((event, index) => (
            <div
              key={index}
              className="bg-white p-4 rounded shadow hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                <div className="flex-grow">
                  <h3 className="font-medium text-lg text-gray-900 mb-2">
                    {event.eventDescription}
                  </h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center">
                      <span className="font-medium w-16">From:</span>
                      <span>{formatDateTime(event.fromDate)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium w-16">To:</span>
                      <span>{formatDateTime(event.toDate)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={event.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    View Details
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {events.length === 0 && !loading && (
        <div className="bg-white p-4 rounded shadow text-center text-gray-500">
          No FDA events found. Click the button above to load events.
        </div>
      )}
    </div>
  );
};

// Main Financial Data Component
const FinancialData = () => {
  const location = useLocation();

  return (
    <div>
      <div className="mb-6">
        <nav className="flex space-x-4 overflow-x-auto">
          <Link
            to="/intel/financial/earnings"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/financial/earnings'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Earnings Calendar
          </Link>
          <Link
            to="/intel/financial/recommendations"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/financial/recommendations'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Analyst Recommendations
          </Link>
          <Link
            to="/intel/financial/basics"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/financial/basics'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Basic Financials
          </Link>
          <Link
            to="/intel/financial/options"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/financial/options'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Options Data
          </Link>
          <Link
            to="/intel/financial/fda"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/financial/fda'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            FDA Calendar
          </Link>
        </nav>
      </div>

      <Routes>
        <Route path="earnings" element={<EarningsCalendar />} />
        <Route path="recommendations" element={<AnalystRecommendations />} />
        <Route path="basics" element={<BasicFinancials />} />
        <Route path="options" element={<OptionsData />} />
        <Route path="fda" element={<FDACalendar />} />
        <Route
          path="*"
          element={<Navigate to="/intel/financial/earnings" replace />}
        />
      </Routes>
    </div>
  );
};

export default FinancialData;
